import React from 'react';
import { X } from 'lucide-react';

const OpEd = ({ articleId, onClose }) => {
  const renderConcertArticle = () => (
    <>
      <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6 leading-tight">
        The Concert You'll Never See
      </h1>
      
      <div className="mb-8 pb-8 border-b border-gray-300">
        <p className="text-lg text-gray-700 font-medium">By Sylvia Jacoby</p>
        <p className="text-sm text-gray-500 mt-1">January 2026</p>
      </div>
      
      <div className="prose prose-lg max-w-none">
        <p className="text-xl text-gray-800 leading-relaxed mb-6 font-serif italic">
          It's July 2025 and Fred Again announces a popup show in New York City.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          The intern world seemingly collapsed.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Are you in the queue?
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          When do the tickets drop?
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          This would be the best night of our entire lives?
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Then… the dreaded…
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          I didn't get it- did you?
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Nope
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-8">
          Shoot. They are going to be so ungodly expensive on StubHub or any other secondary market. Looks like we'll just watch his NPR Tiny Desk concert… again.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Concert tickets used to be physical commodities: tangible pieces of paper that fans could buy in person, with roughly equal opportunity to get a seat. Today, tickets are digital products, and ownership begins the moment a buyer accesses a ticket online. Unfortunately, bots have taken advantage of the digital age of ticketing.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          A ticket bot is not a person clicking faster or getting luckier in a queue. It's software designed to automatically scan ticketing websites and complete purchases almost instantaneously. Instead of one buyer refreshing a page, a single operator can deploy bots to run hundreds or even thousands of purchase attempts at the same time. These programs fill out forms, submit entries, and navigate checkout flows far more quickly than any human could, often slipping past safeguards meant to slow real users down.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Once bots buy up these tickets, they sell them on secondary markets (StubHub, Ticketmaster, etc.) at steep prices, jacking up ticket costs for ordinary fans. According to my Reddit deep dive, the Fred Again tickets we missed out on resold on StubHub for 2.5x their original value.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          In 2016, President Obama signed the Better Online Tickets Sales Act, known as the BOTS Act, specifically to combat this practice. The law made it illegal to use bots to circumvent security measures on ticketing websites and to sell tickets obtained through bots. The goal was to ensure equitable consumer access to live events.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Ten years later, the problem persists. According to the FTC, from 2019 to 2024, nearly $83 billion was spent on tickets through Ticketmaster. Live Nation, Ticketmaster's parent company, controls at least 80 percent of major concert venues' ticketing. Live Nation's monopoly matters because the company has no incentive to fight bots.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Illinois Attorney General Kwame Raoul explains, "Ticketmaster's deceptive business tactics have left fans paying steep hidden fees and pushed them into expensive, secondary ticket markets. While Ticketmaster claims to limit bulk purchases by brokers, it allows its own rules on purchase limits to be broken. The company then profits when those tickets sell for higher prices—and Ticketmaster can collect another round of fees—in its own resale marketplace."
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          This is the convoluted incentive at the heart of the industry. Ticketmaster benefits twice: first from the initial sale, then again from the markup when the same ticket gets resold on its own platform. In August, the FTC sued ticket reseller Key Investment Group for evading purchasing limits to buy thousands of tickets to high demand events including Taylor Swift's Eras tour and resell them at massive markups using thousands of Ticketmaster accounts. Senators Marsha Blackburn and Ben Ray Lujan filed a brief supporting the suit, noting that "bad actors buy up tickets with no intention of using them. They then resell the tickets on secondary markets with eye-popping markups. Their unearned profits come out of the pockets of ordinary Americans who just want to attend a performance by a favorite artist."
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          The solution isn't complicated. Enforcement of the BOTS Act must become a priority, with stricter enforcement and harsher penalties. Ticketmaster should be prohibited from operating its own resale marketplace, eliminating the conflict of interest that incentivizes the company to turn a blind eye while bots prevent real fans from buying the tickets they love at a reasonable price.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-12">
          Until change occurs, young people (and all people!) will keep refreshing browser windows, losing hours to long queues, and scouring secondary markets, only to spend our entire rent on a concert ticket or lose out on formative life experiences.
        </p>
        
        <div className="mt-16 pt-8 border-t border-gray-300">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">References</h3>
          <div className="space-y-4 text-sm text-gray-600">
            <p>
              Bauer, D. C. L. (2025, September 18). Illinois attorney general lawsuit alleges Live Nation, Ticketmaster used deceptive pricing practices. <em>My Journal Courier</em>.<br />
              <a href="https://www.myjournalcourier.com/news/article/live-nation-ticketmaster-illinois-lawsuit-21055454.php" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">
                https://www.myjournalcourier.com/news/article/live-nation-ticketmaster-illinois-lawsuit-21055454.php
              </a>
            </p>
            <p>
              Reuters. (2025, September 15). U.S. probing whether Ticketmaster does enough to stop resale bots.<br />
              <a href="https://www.reuters.com/sustainability/us-probing-whether-ticketmaster-does-enough-stop-resale-bots-bloomberg-news-2025-09-15/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">
                https://www.reuters.com/sustainability/us-probing-whether-ticketmaster-does-enough-stop-resale-bots-bloomberg-news-2025-09-15/
              </a>
            </p>
            <p>
              Reuters. (2026, January 21). U.S. Senate committee to hold ticket pricing hearing with Kid Rock, Live Nation executive.<br />
              <a href="https://www.reuters.com/world/us-senate-committee-hold-ticket-pricing-hearing-ith-kid-rock-live-nation-exec-2026-01-21/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">
                https://www.reuters.com/world/us-senate-committee-hold-ticket-pricing-hearing-ith-kid-rock-live-nation-exec-2026-01-21/
              </a>
            </p>
            <p>
              Reddit user. (2025). Trick for resale market [Online forum post]. Reddit, r/fredagain.<br />
              <a href="https://www.reddit.com/r/fredagain/comments/1ppx196/trick_for_resale_market/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">
                https://www.reddit.com/r/fredagain/comments/1ppx196/trick_for_resale_market/
              </a>
            </p>
            <p>
              Wikipedia contributors. (2025). Better Online Ticket Sales Act. In <em>Wikipedia</em>.<br />
              <a href="https://en.wikipedia.org/wiki/Better_Online_Tickets_Sales_Act" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">
                https://en.wikipedia.org/wiki/Better_Online_Tickets_Sales_Act
              </a>
            </p>
            <p>
              Wong, W., & Woods, D. (2025, October 23). The economic role of reseller bots in the ticket market. <em>NPR</em>.<br />
              <a href="https://www.npr.org/2025/10/23/nx-s1-5561950/the-economic-role-of-reseller-bots-in-the-ticket-market" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">
                https://www.npr.org/2025/10/23/nx-s1-5561950/the-economic-role-of-reseller-bots-in-the-ticket-market
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );

  const renderYearOfAnalog = () => (
    <>
      <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6 leading-tight">
        Year of Analog
      </h1>
      
      <div className="mb-8 pb-8 border-b border-gray-300">
        <p className="text-lg text-gray-700 font-medium">By Sylvia Jacoby</p>
        <p className="text-sm text-gray-500 mt-1">January 2026</p>
      </div>
      
      <div className="prose prose-lg max-w-none">
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          With social media branded as the new cigarette and AI fearmongered as the job-killer of a generation, young people have swung the pendulum back by declaring 2026 the "Year of Analog". A growing number of young people are pursuing offline experiences and limiting screen time in an effort to combat digital dependence and AI fatigue. A recent survey reveals that 50% of Americans have made a conscious effort to disconnect digitally in order to prioritize their well-being.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Do I support the cause? 100%. One month into 2026 and I have picked up watercolor, scrapbooking, linocutting, and creating fridge magnets with air dried clay. I bought myself a Brick and deleted TikTok. I even put my Kindle aside and checked out a stack of paper books from the Duke library. The analog messaging works tremendously well on me, and I truly believe in the positive impacts of limiting my screen time on my attention and well-being.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          However, as a product manager and builder, I cannot help but simultaneously acknowledge the immense value technology brings to our daily lives. This raises the extremely paradoxical question: what role does technology play in the Year of Analog? How can we understand the dangers of digital addiction while simultaneously reaping the benefits of modern technology?
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6 font-semibold">
          The Year of Analog creates space for a new category of products: those designed not to capture attention, but to protect, guide, and enrich it.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          This movement isn't anti-technology, it's anti-unbounded technology. For years, engagement and retention have been the gold standard metrics: how do you build a product people use most frequently for the longest periods of time? But a growing number of products are optimizing for a different measure entirely: value delivered per session.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Take Board, a digital tabletop that lets you browse and download board games to play offline with friends. The product is finite by design. You open it, choose a game, and play, then you're done. Board isn't engineered for endless scrolling or algorithmic nudges. It's engineered for clean retention: users return not because they're hooked, but because the product reliably delivers a specific kind of joy without overstaying its welcome.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Here's the key: Board uses technology to enhance an inherently analog experience. The screen replaces bulky game boxes and simplifies setup, but the actual experience (gathering around a table, taking turns, strategizing face-to-face) remains deeply analog. Technology serves the offline moment rather than competing with it.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          This is the defining characteristic of analog-era products. These products don't compete for attention through dopamine hijacking, they enhance it by serving offline experiences rather than replacing them.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Board's success isn't an anomaly. From the Daylight computer to Lego's smart bricks, more products are embracing the same principle: do one thing well, have a clear stopping point, and actually respect the user's time and attention. They're not designed to pull you back in every five minutes. Instead, they help you accomplish something specific, and then get out of your way. They earn retention through trust, not through gamification or endless feeds.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Some products take it even further: Brick exists purely to block other technology. It's tech designed to protect you from tech itself.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          For builders, this shift invites a rethinking of what success looks like. For years, time-on-platform and frequency of use have been treated as the main proxies for value. But in the Year of Analog, the most trusted products may be the ones consumers use less. They are returned to not out of habit, but because they consistently add value to offline life. The ability to leave easily, and to come back without friction, becomes a feature, not a failure.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          The Year of Analog isn't the death of consumer technology; it's a chance to build products that actually serve us. I'm still using plenty of tech, but I'm using it differently. Pinterest gave me inspiration for my next watercolor piece. Brick has fundamentally changed the way I study. Flint is my go-to spot for podcast and article recommendations.
        </p>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-12">
          In a world full of apps designed to keep us scrolling, 2026 has the potential to introduce a new wave of products that give us a reason to close the screen. Not by being forgettable, but by genuinely adding value, then getting out of the way so we can actually live.
        </p>
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
      <div className="min-h-screen">
        <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-600 uppercase tracking-wider">Op-Ed</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close article"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        <article className="max-w-3xl mx-auto px-6 py-12">
          {articleId === 'year-of-analog' ? renderYearOfAnalog() : renderConcertArticle()}
        </article>
      </div>
    </div>
  );
};

export default OpEd;
